import type { UnifiedRule } from '@/hooks/payment-schedules/usePaymentScheduleEditor';
import type { PaymentScheduleTemplate } from '@/hooks/payment-schedule-templates/usePaymentScheduleTemplate';
import { validateRules } from './PaymentScheduleRulesEditor';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import type {
  PublicPaymentPlanPreview,
  PublicPaymentPlanStage,
} from '@/components/public/PaymentPlanPreview';
import type { CreateEstimateDto } from '@/types/estimate';

export type DraftEstimatePaymentSchedule = NonNullable<
  CreateEstimateDto['paymentScheduleTemplate']
>;

export function paymentScheduleTemplateToDraft(
  template: PaymentScheduleTemplate,
): DraftEstimatePaymentSchedule {
  return {
    rules: template.rules.map((rule) => ({
      trigger: rule.trigger,
      action: rule.action,
      amountSpec: rule.amountSpec,
      autoCharge: rule.autoCharge,
      milestoneRef: rule.milestoneRef,
    })),
    ...(template.previewText ? { previewText: template.previewText } : {}),
  };
}

export function scheduleRuleToUnifiedRule(
  rule: DraftEstimatePaymentSchedule['rules'][number],
): UnifiedRule {
  return {
    ...rule,
    autoCharge: rule.action === 'AUTO_CHARGE',
    milestoneRef: rule.milestoneRef ?? null,
    daysAfter: rule.amountSpec.daysAfter,
    fixedDate: rule.amountSpec.fixedDate,
  };
}

export function validateDraftEstimatePaymentSchedule(
  schedule: DraftEstimatePaymentSchedule,
  grandTotal: number,
  deposit: DraftDepositFacts,
): string[] {
  const rules = schedule.rules.map(scheduleRuleToUnifiedRule);
  return [
    ...validateRules(rules, grandTotal).errors,
    ...validateEstimateTemplateRules(rules, { ...deposit, grandTotal }),
    ...(grandTotal > 0 ? [] : ['Add priced line items before saving a payment schedule.']),
  ];
}

/**
 * What the estimate's deposit clause contributes. Mirrors the server's
 * `EstimateDepositFacts`; `grandTotal` lets a FLAT deposit be expressed as a
 * share of the plan, the same way the server does it.
 */
export interface DraftDepositFacts {
  required: boolean;
  amount: number;
  grandTotal?: number;
}

export function validateEstimateTemplateRules(
  rules: UnifiedRule[],
  deposit: DraftDepositFacts,
): string[] {
  const errors: string[] = [];
  // Mirrors template-validation.ts: `autoCharge` is a mirror column, not an
  // independent switch. The engine dispatches on `action` alone, so no flag can
  // charge a card by itself. Testing the flag here would block a save the
  // server now accepts — a legacy rule carrying CREATE_INVOICE with a stale
  // `autoCharge: true` only ever issues an invoice.
  if (rules.some((rule) => rule.action === 'AUTO_CHARGE') && !deposit.required) {
    errors.push(
      'Auto-charge requires a deposit clause on the estimate. Add a deposit to the estimate before enabling auto-charge.',
    );
  }

  // The ceiling is what the stages INVOICE. The deposit is a payment against
  // the invoices they raise, not a slice of them, so it does not count — see
  // the server's `validateTemplateRules` for why it used to.
  //
  // A `REQUEST_PAYMENT_ONLY` stage is the exception: it raises no invoice for
  // the deposit to land on, so the engine nets the deposit out of what it can
  // ask for. Mirrors the server's two ceilings — the whole plan against the
  // job, and separately the direct-payment stages plus the deposit.
  const grandTotal = deposit.grandTotal ?? 0;
  const depositPercent =
    deposit.required && deposit.amount > 0 && grandTotal > 0
      ? (deposit.amount / grandTotal) * 100
      : 0;
  const rulePercentSum = rules
    .filter((rule) => rule.amountSpec.kind === 'percent_of_job_total')
    .reduce((sum, rule) => sum + (rule.amountSpec.value ?? 0), 0);
  // Dollar stages divide up the same total, so they sit under the same
  // ceiling. Mirrors the server: without this the browser passes a plan the
  // server refuses, and on the create path that rejection discards the whole
  // estimate rather than just the schedule.
  const fixedTotal = rules
    .filter((rule) => rule.amountSpec.kind === 'fixed_amount')
    .reduce((sum, rule) => sum + (rule.amountSpec.value ?? 0), 0);
  const fixedPercent = fixedTotal > 0 && grandTotal > 0 ? (fixedTotal / grandTotal) * 100 : 0;
  const pct = (n: number) => Math.round(n * 100) / 100;
  const percentSum = pct(rulePercentSum + fixedPercent);
  if (percentSum > 100) {
    if (fixedPercent > 0) {
      const parts = [
        rulePercentSum > 0 ? `${rulePercentSum}% in percentage stages` : null,
        `$${fixedTotal.toFixed(2)} in fixed stages`,
      ].filter(Boolean);
      errors.push(
        `This plan allocates ${percentSum}% of the $${grandTotal.toFixed(2)} job — ${parts.join(', ')} — which must be ≤ 100%.`,
      );
    } else {
      errors.push(`Percent rules sum to ${percentSum}% (must be ≤ 100%).`);
    }
  }

  // Second ceiling, mirroring the server: only the direct-payment stages
  // compete with the deposit for the same dollars.
  const directRules = rules.filter((rule) => rule.action === 'REQUEST_PAYMENT_ONLY');
  const directFixedTotal = directRules
    .filter((rule) => rule.amountSpec.kind === 'fixed_amount')
    .reduce((sum, rule) => sum + (rule.amountSpec.value ?? 0), 0);
  const directPercentSum =
    directRules
      .filter((rule) => rule.amountSpec.kind === 'percent_of_job_total')
      .reduce((sum, rule) => sum + (rule.amountSpec.value ?? 0), 0) +
    (directFixedTotal > 0 && grandTotal > 0 ? (directFixedTotal / grandTotal) * 100 : 0);
  const directSum = pct(directPercentSum + depositPercent);
  if (directSum > 100) {
    errors.push(
      `Direct-payment stages ask for ${pct(directPercentSum)}% of the job and the deposit takes ${pct(
        depositPercent,
      )}% — ${directSum}% in all (must be ≤ 100%). A stage that only requests payment raises no invoice for the deposit to be applied to, so it can never collect that share.`,
    );
  }

  // Mirrors the server's zero-consent guard, so the editor says this instead of
  // the save returning a 400 — which on the create path discards the whole
  // estimate rather than just the schedule.
  //
  // Asked from the ORDER-INDEPENDENT worst case, the way
  // `deriveAutoChargeConsentLimits` asks it. The authored-order charge is the
  // wrong question: a stage can net to nothing as authored and still bill real
  // money once another invoice takes the deposit, and refusing that is the
  // false rejection this area keeps re-learning.
  //
  // The wording deliberately avoids "cover". This renders in the same dialog as
  // `PaymentScheduleRulesEditor`'s coverage bar, which reads "Fully covered"
  // whenever a `remaining_balance` stage is present — precisely the shape this
  // refuses. Coverage is whether the stages allocate the job; authorisation is
  // whether the card can be charged. Saying "the deposit covers everything"
  // under a green "Fully covered" reads as a contradiction.
  //
  // Deliberately SOUND BUT INCOMPLETE. The server is authoritative; this must
  // never refuse a plan the server would accept. If the two drift, the failure
  // mode is a case this misses and the server still catches — no worse than
  // having no mirror. The reverse would block a legitimate save in the browser
  // with no way past it.
  if (rules.some((rule) => rule.action === 'AUTO_CHARGE') && grandTotal > 0) {
    const depositAmount = deposit.required ? Math.max(deposit.amount, 0) : 0;
    // The most a stage can invoice under any firing order. A remaining_balance
    // stage firing first takes the whole job; anything else is its authored
    // figure, capped at the job the way the engine caps it.
    const worstCaseGross = (rule: UnifiedRule) => {
      const { kind, value } = rule.amountSpec;
      const authored =
        kind === 'percent_of_job_total'
          ? (grandTotal * (value ?? 0)) / 100
          : kind === 'fixed_amount'
            ? (value ?? 0)
            : grandTotal;
      return Math.max(0, Math.min(authored, grandTotal));
    };
    // Only a stage that raises an invoice can take the deposit off another
    // stage. REQUEST_PAYMENT_ONLY raises none. Nor can remaining_balance: to
    // absorb the deposit it must be invoiced first, and invoiced first it takes
    // the whole job, leaving the stage it was meant to spare nothing to bill.
    const absorbing = rules.filter(
      (rule) =>
        rule.action !== 'REQUEST_PAYMENT_ONLY' &&
        rule.amountSpec.kind !== 'remaining_balance',
    );
    const canBill = rules
      .filter((rule) => rule.action === 'AUTO_CHARGE')
      .some((rule) => {
        const gross = worstCaseGross(rule);
        const elsewhere = Math.max(
          0,
          Math.min(
            absorbing
              .filter((other) => other !== rule)
              .reduce((sum, other) => sum + worstCaseGross(other), 0),
            grandTotal - gross,
          ),
        );
        return gross - Math.max(0, depositAmount - elsewhere) > 0.005;
      });
    if (!canBill) {
      errors.push(
        `This schedule charges the customer's card, but with the $${depositAmount.toFixed(2)} deposit applied, no stage that charges the card can bill anything on this $${grandTotal.toFixed(2)} job — there is nothing left to authorise. Lower the deposit, or change the stages so they do not auto-charge.`,
      );
    }
  }

  // The deposit clause IS the acceptance-time deposit; a stage that repeats it
  // asks the customer twice and raises a duplicate invoice on the job.
  if (
    deposit.required &&
    rules.some(
      (rule) => rule.trigger === 'ON_ESTIMATE_ACCEPTED' && rule.action === 'CREATE_DEPOSIT_INVOICE',
    )
  ) {
    errors.push(
      'This estimate already collects a deposit when the customer accepts. Remove the deposit stage from the payment schedule, or clear the deposit on the estimate.',
    );
  }

  const remainingBalanceRules = rules.filter(
    (rule) => rule.amountSpec.kind === 'remaining_balance',
  );
  if (remainingBalanceRules.length > 1) {
    errors.push('Only one "remaining balance" rule is allowed.');
  }
  if (
    remainingBalanceRules.length === 1 &&
    rules[rules.length - 1]?.amountSpec.kind !== 'remaining_balance'
  ) {
    errors.push('"Remaining balance" must be the last rule.');
  }

  const seenTriggers = new Set<string>();
  const milestoneRefs = new Set<string>();
  for (const rule of rules) {
    if (rule.trigger === 'ON_MILESTONE_COMPLETE') {
      const ref = rule.milestoneRef ?? '';
      if (!ref) {
        errors.push('Milestone rules require a milestone reference.');
        continue;
      }
      if (milestoneRefs.has(ref)) errors.push(`Duplicate milestone rule for "${ref}".`);
      milestoneRefs.add(ref);
      continue;
    }
    if (seenTriggers.has(rule.trigger)) {
      errors.push(`At most one rule per trigger (${rule.trigger.replaceAll('_', ' ')}).`);
    }
    seenTriggers.add(rule.trigger);
  }

  return errors;
}

/**
 * Mirrors the server's `stageLabel` (payment-schedule-templates.service.ts).
 * Kept in step deliberately: the same estimate must not read "Deposit (today)"
 * before save and "Stage 1" after it.
 */
function draftStageLabel(trigger: string, index: number, action: string): string {
  if (trigger === 'ON_ESTIMATE_ACCEPTED' && action.includes('DEPOSIT')) {
    return 'Deposit (today)';
  }
  if (trigger === 'ON_JOB_COMPLETED') return 'Final';
  return 'Stage ' + String(index + 1);
}

/**
 * Mirrors the server triggerDescription.
 *
 * Deliberately NOT the rules editor version: that one is written for the person
 * BUILDING the schedule ("When estimate is accepted"), while this text is read
 * by the CUSTOMER on the quote and the PDF ("When you accept this estimate").
 * Using the editor wording here put both phrasings on screen for one stage.
 */
function draftTriggerDescription(rule: DraftEstimatePaymentSchedule['rules'][number]): string {
  const spec = rule.amountSpec;
  switch (rule.trigger) {
    case 'ON_ESTIMATE_ACCEPTED':
      return 'When you accept this estimate';
    case 'ON_JOB_SCHEDULED':
      return 'When the job is scheduled';
    case 'ON_JOB_STARTED':
      return 'When work begins';
    case 'ON_MILESTONE_COMPLETE':
      return rule.milestoneRef
        ? 'When the "' + rule.milestoneRef + '" milestone is complete'
        : 'On milestone completion';
    case 'ON_JOB_COMPLETED':
      return 'On job completion';
    case 'DAYS_AFTER_COMPLETION': {
      const days = spec.daysAfter;
      return typeof days === 'number'
        ? days + ' day' + (days === 1 ? '' : 's') + ' after completion'
        : 'A configured number of days after completion';
    }
    case 'FIXED_DATE':
      // fixedDate is stored as UTC-midnight and the server formats it in UTC
      // (NOT-230). Formatting in the browser's zone here would print the day
      // before west of UTC, so the preview and the PDF would name different
      // days for the same stage.
      return spec.fixedDate
        ? 'On ' +
            formatDateTimeInTimeZone(spec.fixedDate, 'UTC', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
        : 'On a fixed date';
    default:
      return rule.trigger;
  }
}
/** Mirrors the server's `resolveAmount`, including its 2dp rounding. */
function draftStageAmount(
  spec: { kind: string; value?: number },
  grandTotal: number,
): PublicPaymentPlanStage['amount'] {
  if (spec.kind === 'percent_of_job_total') {
    const pct = spec.value ?? 0;
    return {
      kind: 'percent_of_job_total',
      amount: Math.round(((grandTotal * pct) / 100) * 100) / 100,
    };
  }
  if (spec.kind === 'fixed_amount') {
    return { kind: 'fixed_amount', amount: spec.value ?? 0 };
  }
  return { kind: 'remaining_balance', amount: null };
}

/**
 * Build the customer-facing payment-plan preview from a schedule that only
 * exists in the form.
 *
 * The server preview endpoint needs a saved estimate, so on a new one the PDF
 * preview had nothing to render and silently omitted the plan. Returns null
 * when there is no schedule, which the preview treats as no plan block.
 */
export function draftPaymentPlanPreview(
  schedule: DraftEstimatePaymentSchedule | null | undefined,
  grandTotal: number,
  depositAmount = 0,
): PublicPaymentPlanPreview | null {
  const rules = schedule?.rules ?? [];
  if (rules.length === 0) return null;

  // Net the deposit off the stages the way the server's `resolveStageAmounts`
  // does — held credit lands on whichever invoice issues first, in order — so
  // the figure here is the CHARGE, matching what the saved preview publishes.
  //
  // This used to render gross, and it was survivable only because the ceiling
  // guaranteed stages + deposit <= the job. Now that the stages may allocate
  // the whole job, gross plus the deposit line the document renders overstates
  // the plan: on a $2,400 estimate with a $600 deposit and 40% + 60% stages
  // that is $600 + $960 + $1,440 = $3,000 of plan on a $2,400 job, and it
  // corrected itself the moment the estimate was saved.
  let depositRemaining = Math.max(depositAmount, 0);
  let grossAccounted = 0;

  return {
    // No template row exists yet — nothing consumes this id on the draft path.
    templateId: '',
    previewText: schedule?.previewText ?? null,
    consentRequired: rules.some((rule) => rule.action === 'AUTO_CHARGE'),
    grandTotal,
    // Raw percent column, unknown on the draft path. `paymentPlanRows` reads
    // `depositAmount`, which is the one that has to be here: netting the
    // deposit out of the stages without also declaring it turns an
    // overstatement into an understatement — $360 + $1,440 under a
    // `Total $2,400`.
    depositValue: 0,
    depositAmount: round2(Math.max(depositAmount, 0)),
    stages: rules.map((rule, index) => {
      const gross = draftStageAmount(rule.amountSpec, grandTotal);
      // The engine caps a non-remaining stage at the balance left, and
      // `remaining_balance` IS that balance, so both come out of the same sum.
      const available = round2(Math.max(grandTotal - grossAccounted, 0));
      const grossAmount =
        gross.amount == null ? available : round2(Math.max(Math.min(gross.amount, available), 0));
      // Credit only lands on a stage that raises an invoice. A
      // `REQUEST_PAYMENT_ONLY` stage has no document for it, so the engine
      // resolves it on the `collectible` basis instead — and the direct-payment
      // ceiling above already guarantees the authored figure fits under that,
      // so what it asks for is the authored figure itself. Spending credit
      // against it here rendered $250 for a 50% pay-link stage on a $1,000 job
      // that will ask for $500.
      const invoiceBacked = rule.action !== 'REQUEST_PAYMENT_ONLY';
      const applied = invoiceBacked ? Math.min(depositRemaining, grossAmount) : 0;
      depositRemaining = round2(depositRemaining - applied);
      grossAccounted = round2(grossAccounted + grossAmount);
      return {
        position: index,
        label: draftStageLabel(rule.trigger, index, rule.action),
        triggerDescription: draftTriggerDescription(rule),
        amount: gross.amount == null ? gross : { ...gross, amount: round2(grossAmount - applied) },
        autoCharge: rule.action === 'AUTO_CHARGE',
        action: rule.action,
      };
    }),
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
